import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ContentCopy,
  Visibility,
  CheckCircle,
  Pending,
  Cancel,
  Phone,
  Person,
  Description,
  AccessTime,
  Sync
} from '@mui/icons-material';

const TransactionTable = ({
  transactions,
  onTransactionClick,
  onCopyPixCode,
  canRefreshStatus,
  onAtualizarStatusMercadoPago,
  refreshingStatusId
}) => {
  const formatCurrency = (value) => {
    if (!value) return value;
    return parseFloat(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return phoneNumber;

    const masks = {
      '+55': { name: 'Brasil', mask: '(xx) xxxxx-xxxx' },
      '+54': { name: 'Argentina', mask: '(x) xxxx xx-xxxx' },
      '+595': { name: 'Paraguai', mask: '(xxx) xxx-xxx' },
      '+1': { name: 'EUA', mask: '(xxx) xxx-xxxx' }
    };

    let ddi = Object.keys(masks).find(ddi => phoneNumber.startsWith(ddi));
    if (!ddi) return phoneNumber;

    phoneNumber = phoneNumber.replace(ddi, '');
    const mask = masks[ddi].mask;
    const numericPhoneNumber = phoneNumber.replace(/\D/g, '');

    let formattedPhoneNumber = '';
    let maskIndex = 0;
    for (let i = 0; i < mask.length && maskIndex < numericPhoneNumber.length; i++) {
      if (mask[i] === 'x') {
        formattedPhoneNumber += numericPhoneNumber[maskIndex];
        maskIndex++;
      } else {
        formattedPhoneNumber += mask[i];
      }
    }

    return ddi + ' ' + formattedPhoneNumber;
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Pendente':
        return {
          color: '#2196F3',
          bgColor: '#E3F2FD',
          icon: <Pending sx={{ fontSize: 16 }} />,
          label: 'Pendente'
        };
      case 'PAGO':
        return {
          color: '#4CAF50',
          bgColor: '#E8F5E8',
          icon: <CheckCircle sx={{ fontSize: 16 }} />,
          label: 'Pago'
        };
      case 'Concluído':
        return {
          color: '#4CAF50',
          bgColor: '#E8F5E8',
          icon: <CheckCircle sx={{ fontSize: 16 }} />,
          label: 'Concluído'
        };
      case 'Falhou':
      case 'Expirado':
        return {
          color: '#F44336',
          bgColor: '#FFEBEE',
          icon: <Cancel sx={{ fontSize: 16 }} />,
          label: status
        };
      default:
        return {
          color: '#757575',
          bgColor: '#F5F5F5',
          icon: <Cancel sx={{ fontSize: 16 }} />,
          label: status
        };
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        display: { xs: 'none', md: 'block' },
        borderRadius: 3,
        overflow: 'hidden',
        backgroundColor: '#FAFAFA',
        width: '100%'
      }}
    >
      <TableContainer sx={{ overflowX: 'hidden' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#1976D2' }}>
              <TableCell sx={{
                fontWeight: 'bold',
                color: 'white',
                padding: '8px 6px',
                fontSize: '0.8rem',
                borderBottom: 'none',
                width: '20%'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Description sx={{ mr: 0.5, fontSize: 16 }} />
                  Descrição
                </Box>
              </TableCell>
              <TableCell sx={{
                fontWeight: 'bold',
                color: 'white',
                padding: '8px 6px',
                fontSize: '0.8rem',
                borderBottom: 'none',
                width: '10%'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Person sx={{ mr: 0.5, fontSize: 16 }} />
                  Nome
                </Box>
              </TableCell>
              <TableCell sx={{
                fontWeight: 'bold',
                color: 'white',
                padding: '8px 6px',
                fontSize: '0.8rem',
                borderBottom: 'none',
                width: '6%'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Person sx={{ mr: 0.5, fontSize: 16 }} />
                  IDPDV
                </Box>
              </TableCell>
              <TableCell sx={{
                fontWeight: 'bold',
                color: 'white',
                padding: '8px 6px',
                fontSize: '0.8rem',
                borderBottom: 'none',
                width: '14%'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Person sx={{ mr: 0.5, fontSize: 16 }} />
                  Usuário
                </Box>
              </TableCell>
              <TableCell sx={{
                fontWeight: 'bold',
                color: 'white',
                padding: '8px 6px',
                fontSize: '0.8rem',
                borderBottom: 'none',
                width: '11%'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccessTime sx={{ mr: 0.5, fontSize: 16 }} />
                  Data / Hora
                </Box>
              </TableCell>
              <TableCell sx={{
                fontWeight: 'bold',
                color: 'white',
                padding: '8px 6px',
                fontSize: '0.8rem',
                borderBottom: 'none',
                width: '10%'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Phone sx={{ mr: 0.5, fontSize: 16 }} />
                  Telefone
                </Box>
              </TableCell>
              <TableCell align='right' sx={{
                fontWeight: 'bold',
                color: 'white',
                padding: '8px 6px',
                fontSize: '0.8rem',
                borderBottom: 'none',
                width: '8%'
              }}>
                Valor
              </TableCell>
              <TableCell align='center' sx={{
                fontWeight: 'bold',
                color: 'white',
                padding: '8px 6px',
                fontSize: '0.8rem',
                borderBottom: 'none',
                width: '9%'
              }}>
                Status
              </TableCell>
              <TableCell sx={{
                fontWeight: 'bold',
                color: 'white',
                padding: '8px 4px',
                fontSize: '0.8rem',
                borderBottom: 'none',
                width: '12%'
              }}>
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction, index) => {
              const statusConfig = getStatusConfig(transaction.status);
              return (
                <TableRow
                  key={transaction._id}
                  sx={{
                    backgroundColor: index % 2 === 0 ? 'white' : '#F8F9FA',
                    '&:hover': {
                      backgroundColor: '#E3F2FD',
                      cursor: 'pointer',
                      transform: 'scale(1.01)',
                      transition: 'all 0.2s ease'
                    },
                    borderBottom: '1px solid #E0E0E0'
                  }}
                >
                  <TableCell
                    sx={{
                      padding: '8px 6px',
                      fontSize: '0.8rem',
                      borderBottom: 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    onClick={() => onTransactionClick(transaction)}
                    title={transaction.description || '-'}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                      {transaction.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '8px 6px',
                      fontSize: '0.8rem',
                      borderBottom: 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    onClick={() => onTransactionClick(transaction)}
                    title={transaction.nomeCompleto || '-'}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                      {transaction.nomeCompleto || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '8px 6px',
                      fontSize: '0.8rem',
                      borderBottom: 'none',
                      fontWeight: 'bold',
                      color: '#1976D2'
                    }}
                    onClick={() => onTransactionClick(transaction)}
                  >
                    {transaction.createdBy?.idpdv || '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '8px 6px',
                      fontSize: '0.8rem',
                      borderBottom: 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    onClick={() => onTransactionClick(transaction)}
                    title={transaction.createdBy?.fullname || '-'}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                      {transaction.createdBy?.fullname || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '8px 6px',
                      fontSize: '0.75rem',
                      borderBottom: 'none',
                      color: '#666'
                    }}
                    onClick={() => onTransactionClick(transaction)}
                  >
                    {transaction.date ? new Date(transaction.date).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '8px 6px',
                      fontSize: '0.75rem',
                      borderBottom: 'none',
                      color: '#666'
                    }}
                    onClick={() => onTransactionClick(transaction)}
                  >
                    <span style={{ whiteSpace: 'nowrap' }}>{formatPhoneNumber('+' + transaction.wppNumber)}</span>
                  </TableCell>
                  <TableCell
                    align='right'
                    sx={{
                      fontWeight: 'bold',
                      padding: '8px 6px',
                      fontSize: '0.85rem',
                      borderBottom: 'none',
                      color: '#2E7D32'
                    }}
                    onClick={() => onTransactionClick(transaction)}
                  >
                    {transaction.amount ? formatCurrency(transaction.amount) : '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '8px 6px',
                      borderBottom: 'none'
                    }}
                    onClick={() => onTransactionClick(transaction)}
                  >
                    <Chip
                      icon={statusConfig.icon}
                      label={statusConfig.label}
                      sx={{
                        backgroundColor: statusConfig.bgColor,
                        color: statusConfig.color,
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        height: 24,
                        '& .MuiChip-icon': {
                          color: statusConfig.color,
                          fontSize: 14
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ padding: '8px 4px', borderBottom: 'none' }}>
                    <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center', flexWrap: 'nowrap' }}>
                      <Tooltip title="Ver detalhes">
                        <IconButton
                          size="small"
                          onClick={() => onTransactionClick(transaction)}
                          sx={{
                            color: '#1976D2',
                            padding: 0.375,
                            minWidth: 28,
                            '&:hover': { backgroundColor: '#E3F2FD' }
                          }}
                        >
                          <Visibility sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      {transaction.status === 'Pendente' && (
                        <Tooltip title="Copiar código PIX">
                          <IconButton
                            size="small"
                            onClick={() => onCopyPixCode(transaction.pixCopyPaste)}
                            sx={{
                              color: '#4CAF50',
                              padding: 0.375,
                              minWidth: 28,
                              '&:hover': { backgroundColor: '#E8F5E8' }
                            }}
                          >
                            <ContentCopy sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canRefreshStatus?.(transaction) && onAtualizarStatusMercadoPago && (
                        <Tooltip title="Atualizar status no Mercado Pago">
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onAtualizarStatusMercadoPago(transaction);
                              }}
                              disabled={refreshingStatusId === (transaction.id || transaction._id)}
                              sx={{
                                color: '#FF9800',
                                padding: 0.375,
                                minWidth: 28,
                                '&:hover': { backgroundColor: '#FFF3E0' }
                              }}
                            >
                              <Sync
                                sx={{
                                  fontSize: 16,
                                  animation: refreshingStatusId === (transaction.id || transaction._id) ? 'spin 1s linear infinite' : 'none',
                                  '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } }
                                }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default TransactionTable;
